import axios from "axios";
import { useQuery, useMutation } from "react-query";
import { Config } from "../config";
import { sortingVariablesForCurrentPolicies } from "../accountOwner/pages/OfficePolicyManagement/tabs/constants";

const policyManagementBaseUrl = Config.serviceUrls.policyManagementBaseUrl;

/**
 * @param {number|number[]} ids
 * @returns {number[]}
 */
function normalizePolicyAssignmentIdList(ids) {
  const list = Array.isArray(ids) ? ids : [ids];
  return [
    ...new Set(list.map(Number).filter((id) => Number.isFinite(id) && id > 0)),
  ];
}

export async function officePolicyAction(policyID, policyAction) {
  const response = await axios.post(
    `${policyManagementBaseUrl}/Policy/${policyID}/${policyAction}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

/**
 * Assignment-level action (Cancel, Reassign, etc.) for a policy assignee.
 * @param {number} assignmentId - Policy assignment id (assignee record id)
 * @param {string} action - Action name (e.g. Cancel, Reassign)
 * @param {Object} [params] - Optional payload for the action
 */
export async function policyAssignmentAction(assignmentId, action, params) {
  const response = await axios.post(
    `${policyManagementBaseUrl}/PolicyAssignment/${assignmentId}/${action}`,
    params ?? {},
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  return response.data;
}

/**
 * Get policy assignment (staff view) by assignment id.
 */
export async function getPolicyAssignmentById(assignmentId) {
  if (!assignmentId) return null;
  const response = await axios.get(
    `${policyManagementBaseUrl}/PolicyAssignment/${assignmentId}`,
  );
  if (!response?.data) return null;
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  const raw = response.data?.data ?? response.data;
  return raw && typeof raw === "object" ? raw : null;
}

/**
 * Get comments for a policy assignment.
 * GET …/PolicyAssignmentComment/{assignmentId}?pageNumber=&pageSize=
 */
export async function getPolicyAssignmentComments(
  assignmentId,
  pageNumber = 1,
  pageSize = 10,
) {
  if (!assignmentId) return { data: [], pagination: {} };
  const response = await axios.get(
    `${policyManagementBaseUrl}/PolicyAssignmentComment/${assignmentId}?pageNumber=${pageNumber}&pageSize=${pageSize}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  return response.data?.data ?? response.data;
}

/**
 * Add a comment to a policy assignment.
 */
export async function addPolicyAssignmentComment(assignmentId, params) {
  const response = await axios.post(
    `${policyManagementBaseUrl}/PolicyAssignmentComment/${assignmentId}`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  return response.data;
}

export function usePolicyAssignmentById(assignmentId, options = {}) {
  return useQuery(
    ["policyAssignment", assignmentId],
    () => getPolicyAssignmentById(assignmentId),
    { enabled: !!assignmentId, ...options },
  );
}

export function usePolicyAssignmentComments(
  assignmentId,
  pageNumber = 1,
  pageSize = 10,
  options = {},
) {
  return useQuery(
    ["policyAssignmentComments", assignmentId, pageNumber, pageSize],
    () => getPolicyAssignmentComments(assignmentId, pageNumber, pageSize),
    { enabled: !!assignmentId, ...options },
  );
}

export async function deleteOfficePolicy(policyId) {
  const response = await axios.delete(
    `${policyManagementBaseUrl}/Policy/${policyId}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

/**
 * Add assignees to a policy (e.g. draft).
 * POST Policy/addAssignee
 * @param {number} policyId
 * @param {{ AssigneeIds: number[] }} params - AssigneeIds are staffMemberIds
 */
export async function addPolicyAssignee(policyId, params) {
  const response = await axios.post(
    `${policyManagementBaseUrl}/Policy/addAssignee`,
    { PolicyId: policyId, ...params },
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  return response.data;
}

export async function archiveOfficePolicyFolder(params) {
  const response = await axios.patch(
    `${policyManagementBaseUrl}/PolicyFolder/archived`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export async function archiveUnarchiveOfficePolicyAssignment(
  policyAssignmentId,
  isArchived,
) {
  const response = await axios.patch(
    `${policyManagementBaseUrl}/PolicyAssignment/${policyAssignmentId}/${isArchived ? "Archive" : "Unarchive"}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

/**
 * Archive or unarchive a staff policy assignment (for "Assigned to you" view).
 * PATCH PolicyAssignment/{id}/Archive | Unarchive
 */
export async function archiveStaffOfficePolicies(policyAssignmentId, action) {
  const isArchived = action === "Archive";
  return archiveUnarchiveOfficePolicyAssignment(policyAssignmentId, isArchived);
}

/**
 * Get assignees for a policy (for staff view assignee modal).
 * GET Policy/{policyId}/Assignments
 */
export async function getAssigneeStaff(
  policyId,
  pageSize = 10,
  pageNumber = 1,
) {
  const result = await getPolicyAssignments(policyId, pageNumber, pageSize, {});
  return {
    items: result.items,
    pagination: result.pagination,
  };
}

/**
 * Get staff's assigned policies list (POST PolicyAssignment).
 * Used for "Assigned to you" view.
 */
export async function getStaffOfficePolicyList(
  pageNumber,
  pageSize,
  appliedOfficeFilters = [],
  appliedAssignorFilter = [],
  searchValue = "",
  isArchive = false,
  appliedPriorityFilters = [],
  appliedStatusFilters = [],
  sortBy,
  ownerId,
) {
  const response = await axios.post(
    `${policyManagementBaseUrl}/PolicyAssignment`,
    {
      pageNumber,
      pageSize,
      showArchived: isArchive,
      officeIds: appliedOfficeFilters || [],
      assignorIds: appliedAssignorFilter || [],
      statuses: appliedStatusFilters || [],
      priorities: appliedPriorityFilters || [],
      searchTerm: searchValue || "",
      DateSortBy: sortBy,
      OwnerId: ownerId ?? null,
    },
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  return {
    items: response.data.data ?? [],
    pagination: response.data.pagination ?? {},
  };
}

export function useStaffOfficePolicyList(
  pageNumber,
  pageSize,
  appliedOfficeFilters,
  appliedAssignorFilter,
  searchValue,
  isArchive,
  appliedPriorityFilters,
  appliedStatusFilters,
  sortBy,
  ownerId,
  options = {},
) {
  return useQuery(
    [
      "/officePolicies/mypolicy",
      pageNumber,
      pageSize,
      appliedOfficeFilters,
      appliedAssignorFilter,
      searchValue,
      isArchive,
      appliedPriorityFilters,
      appliedStatusFilters,
      sortBy,
      ownerId,
    ],
    () =>
      getStaffOfficePolicyList(
        pageNumber,
        pageSize,
        appliedOfficeFilters,
        appliedAssignorFilter,
        searchValue,
        isArchive,
        appliedPriorityFilters,
        appliedStatusFilters,
        sortBy,
        ownerId,
      ),
    { staleTime: 0, cacheTime: 0, ...options },
  );
}

/**
 * Office list for policy filters (owner view). GET PolicyFilter/Office
 */
export async function getOfficeList(accountOwnerId, isStaff) {
  const staffBaseUrl = `${policyManagementBaseUrl}/PolicyFilter/Office`;
  const url = accountOwnerId
    ? `${staffBaseUrl}?AccountOwnerId=${accountOwnerId}`
    : staffBaseUrl;
  if (!accountOwnerId && isStaff) {
    return;
  }
  const response = await axios.get(url);
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data.data;
}

export function useOfficeList(accountOwnerId, isStaff, options = {}) {
  return useQuery(
    ["/officePolicies/officeList", accountOwnerId],
    () => getOfficeList(accountOwnerId, isStaff),
    options,
  );
}

/**
 * Assigned staff list for policy filters (owner view). GET PolicyFilter/AssignedUsers
 */
export async function getAssignedStaffList(accountOwnerId, isStaff) {
  const staffBaseUrl = `${policyManagementBaseUrl}/PolicyFilter/AssignedUsers`;
  const url = accountOwnerId
    ? `${staffBaseUrl}?AccountOwnerId=${accountOwnerId}`
    : staffBaseUrl;
  if (!accountOwnerId && isStaff) {
    return;
  }
  const response = await axios.get(url);
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data.data;
}

export function useAssignedStaffList(accountOwnerId, isStaff, options = {}) {
  return useQuery(
    ["/officePolicies/AssignedStaffList", accountOwnerId],
    () => getAssignedStaffList(accountOwnerId, isStaff),
    options,
  );
}

/**
 * Office list for assignee (staff) policy filters. GET PolicyAssignmentFilter/Offices
 */
export async function getAssigneeOfficeList() {
  const response = await axios.get(
    `${policyManagementBaseUrl}/PolicyAssignmentFilter/Offices`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data.data;
}

export function useAssigneeOfficeList(options = {}) {
  return useQuery(
    ["/officePolicies/assigneeOfficeList"],
    () => getAssigneeOfficeList(),
    options,
  );
}

/**
 * Policy owners (assignors) list for staff filters. GET PolicyAssignmentFilter/PolicyOwners
 */
export async function getPolicyOwnersList(ownerId) {
  const baseUrl = `${policyManagementBaseUrl}/PolicyAssignmentFilter/PolicyOwners`;
  const url = ownerId ? `${baseUrl}?OwnerId=${ownerId}` : baseUrl;
  const response = await axios.get(url);
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data.data;
}

export function useAssignorList(ownerId, options = {}) {
  return useQuery(
    ["/PolicyAssignmentFilter/PolicyOwners", ownerId ?? null],
    () => getPolicyOwnersList(ownerId ?? null),
    options,
  );
}

export async function getPolicyAcknowledgementTemplate(ownerId) {
  const response = await axios.get(
    `${policyManagementBaseUrl}/PolicyAcknowledgementTemplate/${ownerId}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export function usePolicyAcknowledgementTemplate(ownerId, options = {}) {
  return useQuery(
    [`/PolicyAcknowledgementTemplate/${ownerId}`, ownerId],
    () => getPolicyAcknowledgementTemplate(ownerId),
    options,
  );
}

export async function addEditPolicyAcknowledgementTemplate(params, edit) {
  const response = await axios[edit ? "put" : "post"](
    `${policyManagementBaseUrl}/PolicyAcknowledgementTemplate`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export async function enablePolicyAcknowledgement(params) {
  const response = await axios.patch(
    `${policyManagementBaseUrl}/PolicyAcknowledgementTemplate/EnableTemplate`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export async function getPolicyCategory(ownerId) {
  const response = await axios.get(
    `${policyManagementBaseUrl}/PolicyCategory?ownerId=${ownerId}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export function usePolicyCategory(ownerId, options = {}) {
  return useQuery(
    [`/PolicyCategory?ownerId=${ownerId}`, ownerId],
    () => getPolicyCategory(ownerId),
    options,
  );
}

export const addPolicyCategory = async (params) => {
  const response = await axios.post(
    `${policyManagementBaseUrl}/PolicyCategory`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
};

export const editPolicyCategory = async (params) => {
  const response = await axios.put(
    `${policyManagementBaseUrl}/PolicyCategory`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
};

export async function deletePolicyCategory(categoryId) {
  const response = await axios.delete(
    `${policyManagementBaseUrl}/PolicyCategory/${categoryId}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

/**
 * Create a policy folder
 */
export const addPolicyFolder = async (params) => {
  const response = await axios.post(
    `${policyManagementBaseUrl}/PolicyFolder`,
    params,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data;
};

/**
 * Get a policy folder by id (for edit).
 */
export const getPolicyFolderById = async (id) => {
  if (!id) return null;
  const response = await axios.get(
    `${policyManagementBaseUrl}/PolicyFolder/${id}`,
  );
  if (!response?.data) return null;
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  const raw = response.data?.data ?? response.data;
  return raw && typeof raw === "object" ? raw : null;
};

/**
 * Latest policy assignment for a folder.
 * `policyId` is optional and is sent only when the caller has one.
 * @param {number|string} policyFolderId
 * @param {number|string} [policyId]
 * @returns {Promise<{ policyFolderId: number, policyId: number, policyAssignmentId: number }>}
 */
export async function getLatestPolicy(policyFolderId, policyId) {
  const params = new URLSearchParams({
    policyFolderId: String(policyFolderId),
  });
  if (policyId != null && policyId !== "") {
    params.set("policyId", String(policyId));
  }
  const response = await axios.get(
    `${policyManagementBaseUrl}/PolicyFolder/latestPolicy?${params.toString()}`,
  );
  const body = response?.data;
  const assignmentId = Number(body?.data?.policyAssignmentId);
  const hasAssignment = Number.isFinite(assignmentId) && assignmentId > 0;
  if (
    !body ||
    body.status === false ||
    body.statusCode !== 200 ||
    !body.data ||
    !hasAssignment
  ) {
    throw new Error(body?.message || "");
  }
  return body.data;
}

/**
 * Latest policy for a folder. Disabled until `policyFolderId` is present.
 * @param {number|string|null} policyFolderId
 * @param {number|string|null} [policyId]
 * @param {Object} [queryOptions]
 */
export function useLatestPolicy(policyFolderId, policyId, queryOptions = {}) {
  return useQuery(
    ["/PolicyFolder/latestPolicy", policyFolderId, policyId ?? null],
    () => getLatestPolicy(policyFolderId, policyId),
    {
      retry: false,
      ...queryOptions,
      enabled:
        policyFolderId != null &&
        policyFolderId !== "" &&
        queryOptions?.enabled !== false,
    },
  );
}

/**
 * Update a policy folder
 */
export const editPolicyFolder = async (params) => {
  const response = await axios.put(
    `${policyManagementBaseUrl}/PolicyFolder`,
    params,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data;
};

/**
 * Delete policy folder(s)
 */
export async function deletePolicyFolder(params) {
  const response = await axios.delete(
    `${policyManagementBaseUrl}/PolicyFolder/deleteSelected`,
    { data: params },
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

/**
 * Re-notify policy/policies by policy IDs.
 * @param {{ policyIds: number[] }} params
 */
export async function reNotifyPolicy(params) {
  const response = await axios.post(
    `${policyManagementBaseUrl}/Policy/renotify`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

/**
 * Create a new policy (policy version).
 * POST /Policy
 * @param {Object} params - Policy payload (title, description, startDate, dueDate, priority, assignees, PolicyFolderId, etc.)
 */
export async function createPolicy(params) {
  const response = await axios.post(
    `${policyManagementBaseUrl}/Policy`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  return response.data;
}

/**
 * Get policy (version) by id for edit.
 * GET /Policy/{id}
 */
export async function getPolicyById(policyId) {
  if (!policyId) return null;
  const response = await axios.get(
    `${policyManagementBaseUrl}/Policy/${policyId}`,
  );
  if (!response?.data) return null;
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  const raw = response.data?.data ?? response.data;
  return raw && typeof raw === "object" ? raw : null;
}

/**
 * Update a policy (policy version).
 * PUT /Policy
 */
export async function updatePolicy(params) {
  const response = await axios.put(`${policyManagementBaseUrl}/Policy`, params);
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message || "Failed to update policy");
  }
  return response.data;
}

/**
 * GET Policy/FilterOptions/Staff - staff list for policy assignees/filters.
 * @param {number} accountOwnerId
 * @param {{ officeId?: number, officeIds?: number[], policyId?: number, isManagerOnly?: boolean }} options
 */
export async function getPolicyFilterOptionsStaff(
  accountOwnerId,
  options = {},
) {
  if (!accountOwnerId) return [];
  const params = new URLSearchParams({ AccountOwnerId: accountOwnerId });
  if (options?.officeId != null) params.append("officeIds", options.officeId);
  if (options?.policyId != null && options?.policyId !== "")
    params.append("policyId", String(options.policyId));
  if (Array.isArray(options?.officeIds) && options.officeIds.length > 0) {
    options.officeIds.forEach((id) => params.append("officeIds", id));
  }
  if (!!options?.isManagerOnly) {
    params.append("isManagerOnly", "true");
  }

  const response = await axios.get(
    `${policyManagementBaseUrl}/Policy/FilterOptions/Staff?${params.toString()}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  const data = response.data?.data ?? response.data;
  return Array.isArray(data) ? data : [];
}

export function usePolicyFilterOptionsStaff(
  accountOwnerId,
  options = {},
  queryOptions = {},
) {
  return useQuery(
    ["/Policy/FilterOptions/Staff", accountOwnerId, options],
    () => getPolicyFilterOptionsStaff(accountOwnerId, options),
    {
      enabled: !!accountOwnerId && queryOptions?.enabled !== false,
      ...queryOptions,
    },
  );
}

/**
 * Get policy assignee list for a policy folder/policy.
 * GET Policy/assigneeList
 * @param {number} policyFolderId - Policy folder id
 * @param {number} ownerId - Account owner id
 * @param {{ officeIds?: number[], isManagerOnly?: boolean, designationId?: number, policyId?: number }} options
 */
export async function getPolicyAssigneeList(
  policyFolderId,
  ownerId,
  options = {},
) {
  if (!policyFolderId || !ownerId) return [];
  const params = new URLSearchParams({
    PolicyFolderId: String(policyFolderId),
    OwnerId: String(ownerId),
  });
  if (Array.isArray(options?.officeIds) && options.officeIds.length > 0) {
    options.officeIds.forEach((id) => params.append("OfficeIds", id));
  }
  if (options?.isManagerOnly === true) {
    params.append("IsManagerOnly", "true");
  }
  if (options?.designationId != null) {
    params.append("DesignationId", String(options.designationId));
  }

  const response = await axios.get(
    `${policyManagementBaseUrl}/Policy/assigneeList?${params.toString()}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  const data = response.data?.data ?? response.data;
  return Array.isArray(data) ? data : [];
}

/**
 * Hook for getting policy assignee list.
 * @param {number} policyFolderId - Policy folder id
 * @param {number} ownerId - Account owner id
 * @param {{ officeIds?: number[], isManagerOnly?: boolean, designationId?: number, policyId?: number }} options
 * @param {Object} queryOptions - React Query options
 */
export function usePolicyAssigneeList(
  policyFolderId,
  ownerId,
  options = {},
  queryOptions = {},
) {
  return useQuery(
    ["/Policy/assigneeList", policyFolderId, ownerId, options],
    () => getPolicyAssigneeList(policyFolderId, ownerId, options),
    {
      enabled: !!policyFolderId && !!ownerId && queryOptions?.enabled !== false,
      ...queryOptions,
    },
  );
}

/** Array params for PolicyFolder/policyList: query param name -> array of ids */
const POLICY_LIST_ARRAY_PARAMS = [
  ["CategoryIds", "appliedCategoryFilters"],
  ["OfficeIds", "appliedOfficeFilters"],
  ["UserIds", "appliedUserIds"],
  ["StatusIds", "appliedStatusFilters"],
];

/**
 * GET /PolicyFolder/policyList
 */
export async function getPolicyFolderList(
  ownerId,
  appliedOfficeFilters = [],
  appliedUserIds = [],
  searchValue = "",
  isArchive = false,
  appliedStatusFilters = [],
  appliedCategoryFilters = [],
  startDate = null,
  endDate = null,
  sortBy = sortingVariablesForCurrentPolicies.Category,
  sortDirection = 1,
  DueDateFilter = 3,
  IsRenew,
) {
  if (!ownerId) return { items: [], pagination: {} };

  const params = new URLSearchParams({
    OwnerId: ownerId,
    SortBy: sortBy,
    IsArchived: isArchive,
    SearchTerm: searchValue || "",
    DueDateFilter: DueDateFilter,
    SortDirection: sortDirection,
    IsRenew,
  });

  const arraySources = {
    appliedCategoryFilters,
    appliedOfficeFilters,
    appliedUserIds,
    appliedStatusFilters,
  };
  POLICY_LIST_ARRAY_PARAMS.forEach(([paramName, key]) => {
    const arr = arraySources[key];
    if (arr?.length) arr.forEach((id) => id && params.append(paramName, id));
  });

  if (startDate) params.append("StartDate", startDate);
  if (endDate) params.append("EndDate", endDate);

  const response = await axios.get(
    `${policyManagementBaseUrl}/PolicyFolder/policyList?${params.toString()}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return {
    items: response.data.data ?? [],
    pagination: response.data.pagination ?? {},
  };
}

export function useGetPolicyFolderList(
  ownerId,
  appliedOfficeFilters = [],
  appliedUserIds = [],
  searchValue = "",
  isArchive = false,
  appliedStatusFilters = [],
  appliedCategoryFilters = [],
  startDate = null,
  endDate = null,
  sortBy = sortingVariablesForCurrentPolicies.Category,
  sortDirection = 1,
  DueDateFilter = 3,
  IsRenew = false,
  options = {},
) {
  const queryArgs = [
    ownerId,
    appliedOfficeFilters,
    appliedUserIds,
    searchValue,
    isArchive,
    appliedStatusFilters,
    appliedCategoryFilters,
    startDate,
    endDate,
    sortBy,
    sortDirection,
    DueDateFilter,
    IsRenew,
  ];
  return useQuery(
    ["/officePolicies/policyFolderList", ...queryArgs],
    () => getPolicyFolderList(...queryArgs),
    { staleTime: 0, cacheTime: 0, ...options },
  );
}

/**
 * Trigger file download from blob.
 * @param {Blob} blob
 * @param {string} defaultFileName
 */
function downloadBlob(blob, defaultFileName = "export.xlsx") {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = defaultFileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Decode base64 string to Blob (e.g. for file_Url from export API).
 * @param {string} base64
 * @param {string} mimeType
 * @returns {Blob}
 */
function base64ToBlob(
  base64,
  mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
}

/**
 * Handle file_Url from export API: open URL in same window (like Staff Attendance timesheet export),
 * or decode base64 and trigger download.
 * @param {string} fileUrl - URL (http/https) or base64 string
 * @param {string} defaultFileName - used when fileUrl is base64
 */
function handleExportFileUrl(fileUrl, defaultFileName = "export.xlsx") {
  if (!fileUrl || typeof fileUrl !== "string") return;
  const trimmed = fileUrl.trim();
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    window.open(trimmed, "_self");
    return;
  }
  const blob = base64ToBlob(trimmed);
  downloadBlob(blob, defaultFileName);
}

/**
 * Export policy folder list (current or archived) as file.
 * GET PolicyFolder/ExportPolicyList - same query params as policyList (no pagination).
 * @param {number} ownerId
 * @param {string} timeZoneId - IANA timezone id for export date formatting
 * @param {Object} params - appliedOfficeFilters, appliedUserIds, searchValue, isArchive, appliedStatusFilters, appliedCategoryFilters, startDate, endDate, sortBy, sortDirection, DueDateFilter, IsRenew
 */
export async function exportPolicyFolderList(
  ownerId,
  timeZoneId,
  {
    appliedOfficeFilters = [],
    appliedUserIds = [],
    searchValue = "",
    isArchive = false,
    appliedStatusFilters = [],
    appliedCategoryFilters = [],
    startDate = null,
    endDate = null,
    sortBy = sortingVariablesForCurrentPolicies.Category,
    sortDirection = 1,
    DueDateFilter = 3,
    IsRenew = false,
  } = {},
) {
  if (!ownerId) throw new Error("OwnerId is required");

  const queryParams = new URLSearchParams({
    OwnerId: ownerId,
    SortBy: sortBy,
    IsArchived: isArchive,
    SearchTerm: searchValue || "",
    DueDateFilter: DueDateFilter,
    SortDirection: sortDirection,
    IsRenew: IsRenew,
  });
  if (timeZoneId) queryParams.append("timeZoneId", String(timeZoneId));
  const arraySources = {
    appliedCategoryFilters,
    appliedOfficeFilters,
    appliedUserIds,
    appliedStatusFilters,
  };
  POLICY_LIST_ARRAY_PARAMS.forEach(([paramName, key]) => {
    const arr = arraySources[key];
    if (arr?.length)
      arr.forEach((id) => id && queryParams.append(paramName, id));
  });
  if (startDate) queryParams.append("StartDate", startDate);
  if (endDate) queryParams.append("EndDate", endDate);

  const response = await axios.get(
    `${policyManagementBaseUrl}/PolicyFolder/ExportPolicyList?${queryParams.toString()}`,
  );

  const data = response?.data;
  if (response.status !== 200) {
    throw new Error(data?.message);
  }
  if (data?.statusCode != null && data.statusCode !== 200) {
    throw new Error(data?.message);
  }

  // Same shape as timesheet export: file_Url may be on data or data.data
  const fileUrl =
    data?.file_Url ??
    data?.data?.file_Url ??
    data?.fileUrl ??
    data?.data?.fileUrl;
  if (!fileUrl || typeof fileUrl !== "string") {
    throw new Error(data?.message);
  }

  handleExportFileUrl(fileUrl, "policy-folder-list.xlsx");
  return response;
}

/**
 * Export policy document (version) list for a folder as file.
 * GET Policy/ExportPolicyVersion - same query params as list (no pagination).
 * @param {number|string} policyFolderId
 * @param {Object} params - officeIds, statuses, searchTerm, userIds, dateUploadedFrom, dateUploadedTo, sortBy, sortDirection
 */
export async function exportPolicyVersion(
  policyFolderId,
  {
    officeIds = [],
    statuses = [],
    searchTerm = "",
    userIds = [],
    dateUploadedFrom = null,
    dateUploadedTo = null,
    sortBy = null,
    sortDirection = 1,
  } = {},
) {
  if (!policyFolderId) throw new Error("policyFolderId is required");

  const params = new URLSearchParams({
    policyFolderId: String(policyFolderId),
    SearchTerm: searchTerm || "",
  });
  (officeIds || []).forEach((id) => params.append("officeIds", id));
  (statuses || []).forEach((id) => params.append("Statuses", id));
  (userIds || []).forEach((id) => params.append("UserIds", id));
  if (dateUploadedFrom) params.append("DateUploadedFrom", dateUploadedFrom);
  if (dateUploadedTo) params.append("DateUploadedTo", dateUploadedTo);
  if (sortBy != null) params.append("SortBy", String(sortBy));
  if (sortDirection != null)
    params.append("SortDirection", String(sortDirection));

  const response = await axios.get(
    `${policyManagementBaseUrl}/Policy/ExportPolicyVersion?${params.toString()}`,
  );

  const data = response?.data;
  if (response.status !== 200) {
    throw new Error(data?.message);
  }
  if (data?.statusCode != null && data.statusCode !== 200) {
    throw new Error(data?.message);
  }

  // Same shape as timesheet export: file_Url may be on data or data.data
  const fileUrl =
    data?.file_Url ??
    data?.data?.file_Url ??
    data?.fileUrl ??
    data?.data?.fileUrl;
  if (!fileUrl || typeof fileUrl !== "string") {
    throw new Error(data?.message);
  }

  handleExportFileUrl(fileUrl, "policy-document-list.xlsx");
}

/**
 * GET policy documents list by policy folder id.
 * API: Policy/listByPolicyFolderId
 * Params: policyFolderId, officeIds, DateUploadedFrom, DateUploadedTo, Statuses, SearchTerm, UserIds
 */
export async function getPolicyVersionList(
  policyFolderId,
  dateUploadedFrom = null,
  dateUploadedTo = null,
  officeIds = [],
  statuses = [],
  searchTerm = "",
  userIds = [],
  sortBy = null,
  sortDirection = 1,
) {
  if (!policyFolderId) return { items: [], pagination: {} };

  const params = new URLSearchParams({
    policyFolderId: String(policyFolderId),
    SearchTerm: searchTerm || "",
  });
  (officeIds || []).forEach((id) => params.append("officeIds", id));
  (statuses || []).forEach((id) => params.append("Statuses", id));
  (userIds || []).forEach((id) => params.append("UserIds", id));
  if (dateUploadedFrom) params.append("DateUploadedFrom", dateUploadedFrom);
  if (dateUploadedTo) params.append("DateUploadedTo", dateUploadedTo);
  if (sortBy != null) params.append("SortBy", String(sortBy));
  if (sortDirection != null)
    params.append("SortDirection", String(sortDirection));

  const response = await axios.get(
    `${policyManagementBaseUrl}/Policy/listByPolicyFolderId?${params.toString()}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  return {
    items: response.data.data ?? [],
    pagination: response.data.pagination ?? {},
  };
}

export function usePolicyVersionList(
  policyFolderId,
  dateUploadedFrom = null,
  dateUploadedTo = null,
  officeIds = [],
  statuses = [],
  searchTerm = "",
  userIds = [],
  sortBy = null,
  sortDirection = 1,
  options = {},
) {
  const queryArgs = [
    policyFolderId,
    dateUploadedFrom,
    dateUploadedTo,
    officeIds,
    statuses,
    searchTerm,
    userIds,
    sortBy,
    sortDirection,
  ];
  return useQuery(
    ["/officePolicies/policyVersionList", ...queryArgs],
    () =>
      getPolicyVersionList(
        policyFolderId,
        dateUploadedFrom,
        dateUploadedTo,
        officeIds,
        statuses,
        searchTerm,
        userIds,
        sortBy,
        sortDirection,
      ),
    {
      staleTime: 0,
      cacheTime: 0,
      enabled: !!policyFolderId && options?.enabled !== false,
      ...options,
    },
  );
}

/**
 * Update policy notification settings.
 * PUT Policy/{policyId}/Notification
 * @param {number} policyId - Policy id
 * @param {Object} params - { notifyAssignees, isNotifyManagement, RemindAfterPublish, RemindBeforeDueDate }
 */
export async function updatePolicyNotification(policyId, params) {
  if (!policyId) throw new Error("Policy id is required");
  const response = await axios.patch(
    `${policyManagementBaseUrl}/Policy/${policyId}/Notification`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  return response.data;
}

/**
 * Get policy assignments (assignees) with pagination.
 * GET Policy/{policyId}/Assignments?pageNumber=&pageSize=
 * @param {number} policyId - Policy id
 * @param {number} pageNumber
 * @param {number} pageSize
 * @param {{ searchTerm?: string, officeId?: number, roleId?: number, designationId?: number, status?: number }} [filters]
 * API expects designationId (roleId in filters is sent as designationId).
 */
export async function getPolicyAssignments(
  policyId,
  pageNumber = 1,
  pageSize = 10,
  filters = {},
) {
  if (!policyId) return { items: [], pagination: {} };
  const params = new URLSearchParams({
    pageNumber: String(pageNumber),
    pageSize: String(pageSize),
  });
  if (filters.searchTerm) params.append("searchTerm", filters.searchTerm);
  if (filters.officeId != null) params.append("officeId", filters.officeId);
  const designationId = filters.designationId ?? filters.roleId;
  if (designationId != null) params.append("designationId", designationId);
  if (filters.status != null) params.append("status", filters.status);

  const response = await axios.get(
    `${policyManagementBaseUrl}/Policy/${policyId}/Assignments?${params.toString()}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  const data = response?.data;
  const items = data?.data ?? [];
  const pagination = data?.pagination || {};
  return {
    items,
    pagination,
  };
}

export function usePolicyAssignments(
  policyId,
  pageNumber = 1,
  pageSize = 10,
  filters = {},
  options = {},
) {
  return useQuery(
    ["policyAssignments", policyId, pageNumber, pageSize, filters],
    () => getPolicyAssignments(policyId, pageNumber, pageSize, filters),
    {
      enabled: !!policyId && options?.enabled !== false,
      ...options,
    },
  );
}

/**
 * Renew policy/policies with acknowledgement settings.
 * POST /Policy/Renew
 * @param {Object} params - Renew policy payload (PolicyIds, StartDate, DueDate, NotifyAssignee, NotifyManagement, etc.)
 */
export async function renewPolicy(params) {
  const response = await axios.post(
    `${policyManagementBaseUrl}/Policy/Renew`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  return response.data;
}

/**
 * Hook for renewing policy/policies.
 * @returns {Object} Mutation object with mutate, mutateAsync, isLoading, etc.
 */
export function useRenewPolicyMutation() {
  return useMutation((params) => renewPolicy(params));
}

/**
 * Renew policy/policies with acknowledgement settings.
 * POST /Policy/Renew
 * @param {Object} params - Renew policy payload (PolicyIds, StartDate, DueDate, NotifyAssignee, NotifyManagement, etc.)
 */
export async function getStaffTracking(params) {
  const response = await axios.post(
    `${policyManagementBaseUrl}/Policy/StaffTracking`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  return response.data;
}
/**
 * Hook for renewing policy/policies.
 * @returns {Object} Mutation object with mutate, mutateAsync, isLoading, etc.
 */
export function useGetStaffTracking(
  {
    ownerId,
    assignedOnStartDate,
    assignedOnEndDate,
    userIds,
    statuses,
    sortBy,
    isDescending,
    officeIds,
    searchTerm,
    IsArchived,
  },
  options = {},
) {
  const queryArgs = [
    ownerId,
    assignedOnStartDate,
    assignedOnEndDate,
    userIds,
    statuses,
    sortBy,
    isDescending,
    officeIds,
    searchTerm,
    IsArchived,
  ];
  return useQuery(
    ["/officePolicies/staffTracking", ...queryArgs],
    () =>
      getStaffTracking({
        ownerId,
        assignedOnStartDate,
        assignedOnEndDate,
        userIds,
        statuses,
        sortBy,
        isDescending,
        officeIds,
        searchTerm,
        IsArchived,
      }),
    { staleTime: 0, cacheTime: 0, ...options },
  );
}

/**
 * GET all policy history for a policy.
 * GET PolicyHistory?PolicyId={policyId}
 * @param {number} policyId - Policy id
 */
export async function getPolicyHistory(policyId) {
  if (!policyId) return { items: [], pagination: {} };
  const params = new URLSearchParams({
    PolicyId: String(policyId),
  });
  const response = await axios.get(
    `${policyManagementBaseUrl}/PolicyHistory?${params.toString()}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  const data = response.data?.data ?? response.data;
  const raw = data && typeof data === "object" ? data : {};
  const items = Array.isArray(raw) ? raw : (raw?.items ?? raw?.data ?? []);
  const pagination = Array.isArray(raw) ? {} : (raw?.pagination ?? {});
  return {
    items: Array.isArray(items) ? items : [],
    pagination: pagination && typeof pagination === "object" ? pagination : {},
  };
}

/**
 * Hook for getting policy history.
 * @param {number} policyId - Policy id
 * @param {Object} options - React Query options
 */
export function usePolicyHistory(policyId, options = {}) {
  return useQuery(
    ["policyHistory", policyId],
    () => getPolicyHistory(policyId),
    {
      enabled: !!policyId && options?.enabled !== false,
      staleTime: 0,
      cacheTime: 0,
      ...options,
    },
  );
}

/**
 * GET policy history details by id.
 * GET PolicyHistory/{id}
 * @param {number} historyId - Policy history id
 */
export async function getPolicyHistoryById(historyId) {
  if (!historyId) return null;
  const response = await axios.get(
    `${policyManagementBaseUrl}/PolicyHistory/${historyId}`,
  );
  if (!response?.data) return null;
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  const raw = response.data?.data ?? response.data;
  return raw && typeof raw === "object" ? raw : null;
}

/**
 * Hook for getting policy history by id.
 * @param {number} historyId - Policy history id
 * @param {Object} options - React Query options
 */
export function usePolicyHistoryById(historyId, options = {}) {
  return useQuery(
    ["policyHistory", historyId],
    () => getPolicyHistoryById(historyId),
    {
      enabled: !!historyId && options?.enabled !== false,
      ...options,
    },
  );
}

/**
 * Export policy history.
 * @param {number} policyId - Policy id
 */
export async function exportPolicyHistory(policyId, timeZoneId) {
  if (!policyId) throw new Error("Policy id is required");
  const params = new URLSearchParams({
    PolicyId: String(policyId),
    timeZoneId: String(timeZoneId),
  });
  const response = await axios.get(
    `${policyManagementBaseUrl}/PolicyHistory/ExportPolicyVersionLog?${params.toString()}`,
    {},
    {
      responseType: "blob",
    },
  );
  if (response.status !== 200) {
    throw new Error(response?.data?.message || response?.message);
  }
  return response.data;
}

/**
 * Hook for exporting policy history.
 * @returns {Object} Mutation object with mutate, mutateAsync, isLoading, etc.
 */
export function useExportPolicyHistory() {
  return useMutation(({ policyId, timeZoneId }) =>
    exportPolicyHistory(policyId, timeZoneId),
  );
}

export async function exportStaffTracking(params, timeZoneId) {
  const response = await axios.post(
    `${policyManagementBaseUrl}/Policy/ExportStaffTracking`,
    { ...params, timeZoneId: String(timeZoneId) },
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message || response?.message);
  }
  return response.data;
}
export function useExportStaffTracking({ params, timeZoneId }, options = {}) {
  return useMutation(() => exportStaffTracking(params, timeZoneId), {
    ...options,
  });
}
// Global
// global logs
const globalLogsHistoryListing = async ({ ownerId }) => {
  const response = await axios.get(
    `${policyManagementBaseUrl}/PolicyFolderHistory?OwnerId=${ownerId}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  return response.data;
};

export function useGlobalLogsHistoryListing(params, options = {}) {
  return useQuery(
    ["globalLogsHistoryListing", params],
    () => globalLogsHistoryListing(params),
    {
      enabled: !!params && options?.enabled !== false,
      ...options,
    },
  );
}

// global log export
const globalLogsHistoryExport = async ({ ownerId, timeZoneId }) => {
  const response = await axios.get(
    `${policyManagementBaseUrl}/PolicyFolderHistory/ExportPolicyHistoryLog?OwnerId=${ownerId}&timeZoneId=${timeZoneId}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  return response.data;
};
export function useGlobalLogsHistoryExport(params, options = {}) {
  return useMutation(() => globalLogsHistoryExport(params), { ...options });
}

/**
 * Get compliance analytics data.
 * GET PolicyFolder/complianceAnalytics
 * @param {number} ownerId - Account owner id
 * @param {Object} params - { CategoryIds?: number[], StartDate?: string, EndDate?: string, OfficeIds?: number[] }
 */
export async function getComplianceAnalytics(ownerId, params = {}) {
  if (!ownerId) return null;
  const queryParams = new URLSearchParams({
    OwnerId: String(ownerId),
  });

  if (
    params.CategoryIds &&
    Array.isArray(params.CategoryIds) &&
    params.CategoryIds.length > 0
  ) {
    params.CategoryIds.forEach((id) => queryParams.append("CategoryIds", id));
  }

  if (params.StartDate) {
    queryParams.append("StartDate", params.StartDate);
  }

  if (params.EndDate) {
    queryParams.append("EndDate", params.EndDate);
  }

  if (
    params.OfficeIds &&
    Array.isArray(params.OfficeIds) &&
    params.OfficeIds.length > 0
  ) {
    params.OfficeIds.forEach((id) => queryParams.append("OfficeIds", id));
  }

  const response = await axios.get(
    `${policyManagementBaseUrl}/PolicyFolder/complianceAnalytics?${queryParams.toString()}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }

  const raw = response.data?.data ?? response.data;
  return raw && typeof raw === "object" ? raw : null;
}

/**
 * Hook for getting compliance analytics data.
 * @param {number} ownerId - Account owner id
 * @param {Object} params - { CategoryIds?: number[], StartDate?: string, EndDate?: string, OfficeIds?: number[] }
 * @param {Object} options - React Query options
 */
export function useComplianceAnalytics(ownerId, params = {}, options = {}) {
  return useQuery(
    ["complianceAnalytics", ownerId, params],
    () => getComplianceAnalytics(ownerId, params),
    {
      enabled: !!ownerId && options?.enabled !== false,
      staleTime: 0,
      cacheTime: 0,
      ...options,
    },
  );
}

/**
 * Get policy analytics data.
 * GET Policy/Analytics?PolicyId={policyId}
 * @param {number} policyId - Policy id
 */
export async function getPolicyAnalytics(policyId) {
  if (!policyId) return null;
  const response = await axios.get(
    `${policyManagementBaseUrl}/Policy/Analytics?PolicyId=${policyId}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }

  const raw = response.data?.data ?? response.data;
  return raw && typeof raw === "object" ? raw : null;
}

/**
 * Hook for getting policy analytics data.
 * @param {number} policyId - Policy id
 * @param {Object} options - React Query options
 */
export function usePolicyAnalytics(policyId, options = {}) {
  return useQuery(
    ["policyAnalytics", policyId],
    () => getPolicyAnalytics(policyId),
    {
      enabled: !!policyId && options?.enabled !== false,
      staleTime: 0,
      cacheTime: 0,
      ...options,
    },
  );
}

/**
 * Get default policies (quick policies).
 * GET DefaultPolicy/getDefaultPolicies?OwnerId={ownerId}
 * @param {number} ownerId - Account owner id
 */
export async function getDefaultPolicies(ownerId) {
  if (!ownerId) return [];
  const response = await axios.get(
    `${policyManagementBaseUrl}/DefaultPolicy/getDefaultPolicies?OwnerId=${ownerId}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  const data = response.data?.data ?? response.data;
  return Array.isArray(data) ? data : [];
}

/**
 * Hook for getting default policies (quick policies).
 * @param {number} ownerId - Account owner id
 * @param {Object} options - React Query options
 */
export function useDefaultPolicies(ownerId, options = {}) {
  return useQuery(
    ["defaultPolicies", ownerId],
    () => getDefaultPolicies(ownerId),
    {
      enabled: !!ownerId && options?.enabled !== false,
      staleTime: 0,
      cacheTime: 0,
      ...options,
    },
  );
}

/**
 * Add policy folder by default (quick policy).
 * POST DefaultPolicy/addPolicyFolderByDefault
 * @param {Object} params - { OwnerId: number, Items: Array<{ DefaultPolicyCategoryId: number, DefaultPolicyFolderId: number }> }
 */
export async function addPolicyFolderByDefault(params) {
  const response = await axios.post(
    `${policyManagementBaseUrl}/DefaultPolicy/addPolicyFolderByDefault`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  return response.data;
}

/**
 * Hook for adding policy folder by default (quick policy).
 * @returns {Object} Mutation object with mutate, mutateAsync, isLoading, etc.
 */
export function useAddPolicyFolderByDefaultMutation() {
  return useMutation((params) => addPolicyFolderByDefault(params));
}

/**
 * Staff Tracking — re-notify assignees (batch).
 * POST /PolicyAssignment/StaffTrackingReNotify
 * @param {number[]} policyAssignmentIds
 */
export async function staffTrackingReNotify(policyAssignmentIds) {
  const ids = normalizePolicyAssignmentIdList(policyAssignmentIds);
  if (!ids.length) throw new Error("Policy assignment id is required");
  const response = await axios.post(
    `${policyManagementBaseUrl}/PolicyAssignment/StaffTrackingReNotify`,
    { PolicyAssignmentIds: ids },
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  return response.data;
}

/**
 * Staff Tracking — archive or unarchive assignments (batch).
 * PATCH /PolicyAssignment/Archive
 * @param {number[]} policyAssignmentIds
 * @param {boolean} isArchived
 */
export async function staffTrackingArchiveAssignments(
  policyAssignmentIds,
  isArchived,
) {
  const ids = normalizePolicyAssignmentIdList(policyAssignmentIds);
  if (!ids.length) throw new Error("Policy assignment id is required");
  const response = await axios.patch(
    `${policyManagementBaseUrl}/PolicyAssignment/Archive`,
    { PolicyAssignmentIds: ids, IsArchived: !!isArchived },
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  return response.data;
}

/**
 * Staff Tracking — cancel assignments (batch).
 * POST /PolicyAssignment/Cancel
 * @param {number[]} policyAssignmentIds
 */
export async function cancelStaffTrackingAssignments(policyAssignmentIds) {
  const ids = normalizePolicyAssignmentIdList(policyAssignmentIds);
  if (!ids.length) throw new Error("Policy assignment id is required");
  const response = await axios.post(
    `${policyManagementBaseUrl}/PolicyAssignment/Cancel`,
    { PolicyAssignmentIds: ids },
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data?.message);
  }
  return response.data;
}

/**
 * Single-assignment re-notify for Staff Tracking (delegates to batch API).
 * @param {number} policyAssignmentId
 */
export async function reNotifyAssigneePolicy(policyAssignmentId) {
  return staffTrackingReNotify([policyAssignmentId]);
}
